import React from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { PALETTE } from './constants';
import { DashboardLoading, daysSince, ownerCount, ownerName, sumCounts } from './dashboard/dashboardFormatting';
import FocusTable, { Chip } from './dashboard/FocusTable';
import HBars from './dashboard/HBars';
import RadioDashboardSkeleton from './dashboard/RadioDashboardSkeleton';
import RadioDonut from './dashboard/RadioDonut';
import { useGeneralRisk } from './hooks/useGeneralRisk';

// IB-677 rework — Decisions dashboard on the shared skeleton. Profile = priority
// split; key metric = age profile; focus table = high-priority decisions.

export default function GeneralRiskDashboardDecisions() {
    const { dashboardData, loading } = useGeneralRisk();

    if (loading.dashboard) {
        return <DashboardLoading />;
    }

    const decisions = dashboardData?.decisions || {};
    const overview = dashboardData?.overview || {};
    const priorities = decisions.open_by_priority_counts || {};
    const open = sumCounts(priorities);
    const buckets = decisions.age_buckets || {};

    const tiles = [
        { lead: true, label: $t('Open decisions'), value: open, sub: `${priorities.high ?? 0} ${$t('high priority')}` },
        { label: $t('High priority'), value: priorities.high ?? 0 },
        { tone: 'red', label: $t('Ageing > 30 days'), value: decisions.ageing_over_30_count ?? 0 },
        {
            tone: 'warn',
            label: $t('Overdue actions'),
            value: decisions.overdue_actions_count ?? 0,
            sub: `${$t('across')} ${ownerCount(decisions.overdue_actions_count_by_owner)} ${$t('owners')}`,
        },
        { label: $t('Open actions'), value: decisions.open_actions_count ?? 0 },
    ];

    const profile = {
        title: $t('Priority profile'),
        content: (
            <RadioDonut
                total={`${open} ${$t('pending')}`}
                segments={[
                    { value: priorities.high ?? 0, colour: PALETTE.red, label: `${$t('High')} · ${priorities.high ?? 0}` },
                    { value: priorities.medium ?? 0, colour: PALETTE.blue50, label: `${$t('Medium')} · ${priorities.medium ?? 0}` },
                    { value: priorities.low ?? 0, colour: PALETTE.charcoal25, label: `${$t('Low')} · ${priorities.low ?? 0}` },
                ]}
            />
        ),
    };

    const keyMetric = {
        title: $t('Age profile'),
        content: (
            <HBars
                labelWidth={64}
                rows={[
                    { label: $t('0–14d'), segments: [{ value: buckets['0-14'] ?? 0, colourClass: 'tw-bg-blue-50' }] },
                    { label: $t('15–30d'), segments: [{ value: buckets['15-30'] ?? 0, colourClass: 'tw-bg-blue' }] },
                    { label: $t('>30d'), segments: [{ value: buckets['30+'] ?? 0, colourClass: 'tw-bg-red' }] },
                ]}
                emptyLabel={$t('No open decisions')}
            />
        ),
    };

    const highPriorities = decisions.high_priorities || [];
    const focus = (
        <FocusTable
            title={$t('High priority decisions')}
            columns={[$t('Decision'), $t('Priority'), $t('Age'), $t('Owner')]}
            rows={highPriorities.map((decision) => [
                decision.description || decision.id,
                <Chip key="p">{decision.priority || '—'}</Chip>,
                `${daysSince(decision.created_at) ?? 0} ${$t('days')}`,
                ownerName(decision.owner),
            ])}
            empty={$t('No high priority decisions')}
        />
    );

    return (
        <RadioDashboardSkeleton
            tiles={tiles}
            profile={profile}
            movement={{ data: overview.movement, category: 'decisions' }}
            keyMetric={keyMetric}
            focus={focus}
        />
    );
}

if (document.getElementById('generalRiskDashboardDecisions')) {
    const root = document.getElementById('generalRiskDashboardDecisions');
    ReactDOM.render(
        <App>
            <GeneralRiskDashboardDecisions />
        </App>,
        root,
    );
}
